/* tslint:disable */
/* eslint-disable */
/**
 * Scrivio Client States API
 * API for managing client states (cliëntstatussen) within an organization.  Client states define the lifecycle stages that clients progress through, such as Lead, Intake, Actief, Afsluiting, and Afgesloten.  States are organization-configurable, allowing each organization to define their own set of states with custom names and colors.  ## State History (Versioned Tracking) Client state changes are tracked historically with effective dates. Each change creates a new state period. The end date of each period is derived from the next period\'s start date minus 1 day (per #296).  ## Authentication All endpoints require Firebase Authentication JWT token in `Authorization: Bearer <token>` header.  ## Error Responses All error responses follow RFC 7807 Problem Details format. 
 *
 * The version of the OpenAPI document: 1.0.0
 * Contact: support@scrivio.nl
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import type { Configuration } from './configuration';
import type { AxiosPromise, AxiosInstance, RawAxiosRequestConfig } from 'axios';
import globalAxios from 'axios';
// Some imports not used depending on template conditions
// @ts-ignore
import { DUMMY_BASE_URL, assertParamExists, setApiKeyToObject, setBasicAuthToObject, setBearerAuthToObject, setOAuthToObject, setSearchParams, serializeDataIfNeeded, toPathString, createRequestFunction } from './common';
import type { RequestArgs } from './base';
// @ts-ignore
import { BASE_PATH, COLLECTION_FORMATS, BaseAPI, RequiredError, operationServerMap } from './base';

export interface ChangeClientStateRequest {
    /**
     * ID of the new state
     */
    'clientStateId': string;
    /**
     * Effective date for the state change (must be after current period\'s start)
     */
    'startdatum': string;
    /**
     * Optional reason for the state change
     */
    'reden'?: string | null;
}
export interface ClientStateHistoryResponse {
    /**
     * Client ID
     */
    'clientId': string;
    'current'?: ClientStatePeriodResponse;
    /**
     * All state periods ordered by startdatum descending
     */
    'history': Array<ClientStatePeriodResponse>;
    /**
     * Number of state changes (excluding initial state). Computed for display in the Scrivio app.
     */
    'wijzigingCount': number;
}
export interface ClientStateListResponse {
    'content': Array<ClientStateResponse>;
    /**
     * Current page number (0-indexed)
     */
    'page': number;
    /**
     * Number of items per page
     */
    'size': number;
    /**
     * Total number of items
     */
    'totalElements': number;
    /**
     * Total number of pages
     */
    'totalPages': number;
}
export interface ClientStatePeriodResponse {
    /**
     * Period unique identifier
     */
    'id': string;
    /**
     * Client this period belongs to
     */
    'clientId': string;
    /**
     * Reference to the client state
     */
    'clientStateId': string;
    /**
     * Name of the state (resolved from clientStateId)
     */
    'stateName'?: string | null;
    /**
     * Color of the state
     */
    'stateColor'?: string | null;
    /**
     * Effective start date of this period
     */
    'startdatum': string;
    /**
     * Derived end date (null means current/active period)
     */
    'einddatum'?: string | null;
    /**
     * Optional reason for the state change
     */
    'reden'?: string | null;
    /**
     * True when this is the current (open-ended) period
     */
    'isActive': boolean;
    /**
     * Timestamp when created (milliseconds since epoch)
     */
    'createdAt': number;
    /**
     * Timestamp when last updated (milliseconds since epoch)
     */
    'updatedAt': number;
}
export interface ClientStateResponse {
    /**
     * Client state unique identifier
     */
    'id': string;
    /**
     * Organization this state belongs to
     */
    'organizationId': string;
    /**
     * State name (e.g., \"Lead\", \"Intake\", \"Actief\")
     */
    'name': string;
    /**
     * Hex color code for visual identification
     */
    'color'?: string | null;
    /**
     * Order in which states appear in lists
     */
    'displayOrder': number;
    /**
     * Whether this state can be assigned to clients
     */
    'isActive': boolean;
    /**
     * If true, this state is assigned to new clients
     */
    'isDefault': boolean;
    /**
     * Timestamp when created (milliseconds since epoch)
     */
    'createdAt': number;
    /**
     * Timestamp when last updated (milliseconds since epoch)
     */
    'updatedAt': number;
}
export interface CreateClientStateRequest {
    /**
     * State name
     */
    'name': string;
    /**
     * Hex color code
     */
    'color'?: string | null;
    /**
     * Display order
     */
    'displayOrder'?: number;
    /**
     * Whether the state is active
     */
    'isActive'?: boolean;
    /**
     * Whether this is the default state for new clients
     */
    'isDefault'?: boolean;
}
/**
 * RFC 7807 Problem Details for HTTP APIs
 */
export interface ProblemDetail {
    /**
     * URI reference identifying the problem type
     */
    'type': string;
    /**
     * Short, human-readable summary
     */
    'title': string;
    /**
     * HTTP status code
     */
    'status': number;
    /**
     * Human-readable explanation
     */
    'detail'?: string;
    /**
     * URI reference identifying the specific occurrence
     */
    'instance'?: string;
    /**
     * List of validation errors
     */
    'errors'?: Array<ProblemDetailErrorsInner>;
}
export interface ProblemDetailErrorsInner {
    /**
     * Field name that failed validation
     */
    'field'?: string;
    /**
     * Error message
     */
    'message'?: string;
}
/**
 * Update the start date or reason of a state period
 */
export interface UpdateClientStatePeriodRequest {
    /**
     * New start date for this period
     */
    'startdatum'?: string | null;
    /**
     * Reason for the state change
     */
    'reden'?: string | null;
}
/**
 * All fields are optional. Only provided fields will be updated.
 */
export interface UpdateClientStateRequest {
    /**
     * State name
     */
    'name'?: string | null;
    /**
     * Hex color code
     */
    'color'?: string | null;
    /**
     * Display order
     */
    'displayOrder'?: number | null;
    /**
     * Whether the state is active
     */
    'isActive'?: boolean | null;
    /**
     * Whether this is the default state
     */
    'isDefault'?: boolean | null;
}

/**
 * ClientStatePeriodsApi - axios parameter creator
 */
export const ClientStatePeriodsApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Change a client\'s state by creating a new state period. The end date of the current period will be derived from the new period\'s start date. (Requires clients.update permission) 
         * @summary Change client state
         * @param {string} clientId Client UUID
         * @param {ChangeClientStateRequest} changeClientStateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        changeClientState: async (clientId: string, changeClientStateRequest: ChangeClientStateRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('changeClientState', 'clientId', clientId)
            // verify required parameter 'changeClientStateRequest' is not null or undefined
            assertParamExists('changeClientState', 'changeClientStateRequest', changeClientStateRequest)
            const localVarPath = `/api/v1/clients/{clientId}/state`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(changeClientStateRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns the full state history of a client
         * @summary Get client state history
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getClientStateHistory: async (clientId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('getClientStateHistory', 'clientId', clientId)
            const localVarPath = `/api/v1/clients/{clientId}/state/history`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns the current state of a client. A client without one answers 204, so 404 keeps meaning that the client itself was not found. 
         * @summary Get current client state
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getCurrentClientState: async (clientId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('getCurrentClientState', 'clientId', clientId)
            const localVarPath = `/api/v1/clients/{clientId}/state`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Update a state period\'s start date or reason. Allows editing historical dates. (Requires clients.update permission) 
         * @summary Update state period
         * @param {string} clientId Client UUID
         * @param {string} periodId State period UUID
         * @param {UpdateClientStatePeriodRequest} updateClientStatePeriodRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateClientStatePeriod: async (clientId: string, periodId: string, updateClientStatePeriodRequest: UpdateClientStatePeriodRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientId' is not null or undefined
            assertParamExists('updateClientStatePeriod', 'clientId', clientId)
            // verify required parameter 'periodId' is not null or undefined
            assertParamExists('updateClientStatePeriod', 'periodId', periodId)
            // verify required parameter 'updateClientStatePeriodRequest' is not null or undefined
            assertParamExists('updateClientStatePeriod', 'updateClientStatePeriodRequest', updateClientStatePeriodRequest)
            const localVarPath = `/api/v1/clients/{clientId}/state/periods/{periodId}`
                .replace(`{${"clientId"}}`, encodeURIComponent(String(clientId)))
                .replace(`{${"periodId"}}`, encodeURIComponent(String(periodId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateClientStatePeriodRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ClientStatePeriodsApi - functional programming interface
 */
export const ClientStatePeriodsApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ClientStatePeriodsApiAxiosParamCreator(configuration)
    return {
        /**
         * Change a client\'s state by creating a new state period. The end date of the current period will be derived from the new period\'s start date. (Requires clients.update permission) 
         * @summary Change client state
         * @param {string} clientId Client UUID
         * @param {ChangeClientStateRequest} changeClientStateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async changeClientState(clientId: string, changeClientStateRequest: ChangeClientStateRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ClientStatePeriodResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.changeClientState(clientId, changeClientStateRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientStatePeriodsApi.changeClientState']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns the full state history of a client
         * @summary Get client state history
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getClientStateHistory(clientId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ClientStateHistoryResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getClientStateHistory(clientId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientStatePeriodsApi.getClientStateHistory']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns the current state of a client. A client without one answers 204, so 404 keeps meaning that the client itself was not found. 
         * @summary Get current client state
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getCurrentClientState(clientId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ClientStatePeriodResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getCurrentClientState(clientId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientStatePeriodsApi.getCurrentClientState']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Update a state period\'s start date or reason. Allows editing historical dates. (Requires clients.update permission) 
         * @summary Update state period
         * @param {string} clientId Client UUID
         * @param {string} periodId State period UUID
         * @param {UpdateClientStatePeriodRequest} updateClientStatePeriodRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateClientStatePeriod(clientId: string, periodId: string, updateClientStatePeriodRequest: UpdateClientStatePeriodRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ClientStatePeriodResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateClientStatePeriod(clientId, periodId, updateClientStatePeriodRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientStatePeriodsApi.updateClientStatePeriod']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * ClientStatePeriodsApi - factory interface
 */
export const ClientStatePeriodsApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ClientStatePeriodsApiFp(configuration)
    return {
        /**
         * Change a client\'s state by creating a new state period. The end date of the current period will be derived from the new period\'s start date. (Requires clients.update permission) 
         * @summary Change client state
         * @param {string} clientId Client UUID
         * @param {ChangeClientStateRequest} changeClientStateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        changeClientState(clientId: string, changeClientStateRequest: ChangeClientStateRequest, options?: RawAxiosRequestConfig): AxiosPromise<ClientStatePeriodResponse> {
            return localVarFp.changeClientState(clientId, changeClientStateRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns the full state history of a client
         * @summary Get client state history
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getClientStateHistory(clientId: string, options?: RawAxiosRequestConfig): AxiosPromise<ClientStateHistoryResponse> {
            return localVarFp.getClientStateHistory(clientId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns the current state of a client. A client without one answers 204, so 404 keeps meaning that the client itself was not found. 
         * @summary Get current client state
         * @param {string} clientId Client UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getCurrentClientState(clientId: string, options?: RawAxiosRequestConfig): AxiosPromise<ClientStatePeriodResponse> {
            return localVarFp.getCurrentClientState(clientId, options).then((request) => request(axios, basePath));
        },
        /**
         * Update a state period\'s start date or reason. Allows editing historical dates. (Requires clients.update permission) 
         * @summary Update state period
         * @param {string} clientId Client UUID
         * @param {string} periodId State period UUID
         * @param {UpdateClientStatePeriodRequest} updateClientStatePeriodRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateClientStatePeriod(clientId: string, periodId: string, updateClientStatePeriodRequest: UpdateClientStatePeriodRequest, options?: RawAxiosRequestConfig): AxiosPromise<ClientStatePeriodResponse> {
            return localVarFp.updateClientStatePeriod(clientId, periodId, updateClientStatePeriodRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * ClientStatePeriodsApi - object-oriented interface
 */
export class ClientStatePeriodsApi extends BaseAPI {
    /**
     * Change a client\'s state by creating a new state period. The end date of the current period will be derived from the new period\'s start date. (Requires clients.update permission) 
     * @summary Change client state
     * @param {string} clientId Client UUID
     * @param {ChangeClientStateRequest} changeClientStateRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public changeClientState(clientId: string, changeClientStateRequest: ChangeClientStateRequest, options?: RawAxiosRequestConfig) {
        return ClientStatePeriodsApiFp(this.configuration).changeClientState(clientId, changeClientStateRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns the full state history of a client
     * @summary Get client state history
     * @param {string} clientId Client UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getClientStateHistory(clientId: string, options?: RawAxiosRequestConfig) {
        return ClientStatePeriodsApiFp(this.configuration).getClientStateHistory(clientId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns the current state of a client. A client without one answers 204, so 404 keeps meaning that the client itself was not found. 
     * @summary Get current client state
     * @param {string} clientId Client UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getCurrentClientState(clientId: string, options?: RawAxiosRequestConfig) {
        return ClientStatePeriodsApiFp(this.configuration).getCurrentClientState(clientId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Update a state period\'s start date or reason. Allows editing historical dates. (Requires clients.update permission) 
     * @summary Update state period
     * @param {string} clientId Client UUID
     * @param {string} periodId State period UUID
     * @param {UpdateClientStatePeriodRequest} updateClientStatePeriodRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateClientStatePeriod(clientId: string, periodId: string, updateClientStatePeriodRequest: UpdateClientStatePeriodRequest, options?: RawAxiosRequestConfig) {
        return ClientStatePeriodsApiFp(this.configuration).updateClientStatePeriod(clientId, periodId, updateClientStatePeriodRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * ClientStatesApi - axios parameter creator
 */
export const ClientStatesApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Create a new client state (requires client_states.create permission)
         * @summary Create client state
         * @param {CreateClientStateRequest} createClientStateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createClientState: async (createClientStateRequest: CreateClientStateRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'createClientStateRequest' is not null or undefined
            assertParamExists('createClientState', 'createClientStateRequest', createClientStateRequest)
            const localVarPath = `/api/v1/client-states`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(createClientStateRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Delete/deactivate a client state (requires client_states.delete permission). If the state is in use by clients, it will be deactivated instead of deleted. 
         * @summary Delete client state
         * @param {string} clientStateId Client state UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteClientState: async (clientStateId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientStateId' is not null or undefined
            assertParamExists('deleteClientState', 'clientStateId', clientStateId)
            const localVarPath = `/api/v1/client-states/{clientStateId}`
                .replace(`{${"clientStateId"}}`, encodeURIComponent(String(clientStateId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns a client state by ID
         * @summary Get client state by ID
         * @param {string} clientStateId Client state UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getClientStateById: async (clientStateId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientStateId' is not null or undefined
            assertParamExists('getClientStateById', 'clientStateId', clientStateId)
            const localVarPath = `/api/v1/client-states/{clientStateId}`
                .replace(`{${"clientStateId"}}`, encodeURIComponent(String(clientStateId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns all client states for the authenticated user\'s organization
         * @summary List client states
         * @param {boolean} [activeOnly] If true, only return active client states
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of items per page
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listClientStates: async (activeOnly?: boolean, page?: number, size?: number, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/client-states`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (activeOnly !== undefined) {
                localVarQueryParameter['activeOnly'] = activeOnly;
            }

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (size !== undefined) {
                localVarQueryParameter['size'] = size;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Update a client state (requires client_states.update permission)
         * @summary Update client state
         * @param {string} clientStateId Client state UUID
         * @param {UpdateClientStateRequest} updateClientStateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateClientState: async (clientStateId: string, updateClientStateRequest: UpdateClientStateRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'clientStateId' is not null or undefined
            assertParamExists('updateClientState', 'clientStateId', clientStateId)
            // verify required parameter 'updateClientStateRequest' is not null or undefined
            assertParamExists('updateClientState', 'updateClientStateRequest', updateClientStateRequest)
            const localVarPath = `/api/v1/client-states/{clientStateId}`
                .replace(`{${"clientStateId"}}`, encodeURIComponent(String(clientStateId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateClientStateRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * ClientStatesApi - functional programming interface
 */
export const ClientStatesApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = ClientStatesApiAxiosParamCreator(configuration)
    return {
        /**
         * Create a new client state (requires client_states.create permission)
         * @summary Create client state
         * @param {CreateClientStateRequest} createClientStateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createClientState(createClientStateRequest: CreateClientStateRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ClientStateResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createClientState(createClientStateRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientStatesApi.createClientState']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Delete/deactivate a client state (requires client_states.delete permission). If the state is in use by clients, it will be deactivated instead of deleted. 
         * @summary Delete client state
         * @param {string} clientStateId Client state UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteClientState(clientStateId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteClientState(clientStateId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientStatesApi.deleteClientState']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns a client state by ID
         * @summary Get client state by ID
         * @param {string} clientStateId Client state UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getClientStateById(clientStateId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ClientStateResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getClientStateById(clientStateId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientStatesApi.getClientStateById']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns all client states for the authenticated user\'s organization
         * @summary List client states
         * @param {boolean} [activeOnly] If true, only return active client states
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of items per page
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listClientStates(activeOnly?: boolean, page?: number, size?: number, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ClientStateListResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listClientStates(activeOnly, page, size, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientStatesApi.listClientStates']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Update a client state (requires client_states.update permission)
         * @summary Update client state
         * @param {string} clientStateId Client state UUID
         * @param {UpdateClientStateRequest} updateClientStateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateClientState(clientStateId: string, updateClientStateRequest: UpdateClientStateRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ClientStateResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateClientState(clientStateId, updateClientStateRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['ClientStatesApi.updateClientState']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * ClientStatesApi - factory interface
 */
export const ClientStatesApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = ClientStatesApiFp(configuration)
    return {
        /**
         * Create a new client state (requires client_states.create permission)
         * @summary Create client state
         * @param {CreateClientStateRequest} createClientStateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createClientState(createClientStateRequest: CreateClientStateRequest, options?: RawAxiosRequestConfig): AxiosPromise<ClientStateResponse> {
            return localVarFp.createClientState(createClientStateRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Delete/deactivate a client state (requires client_states.delete permission). If the state is in use by clients, it will be deactivated instead of deleted. 
         * @summary Delete client state
         * @param {string} clientStateId Client state UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteClientState(clientStateId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteClientState(clientStateId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns a client state by ID
         * @summary Get client state by ID
         * @param {string} clientStateId Client state UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getClientStateById(clientStateId: string, options?: RawAxiosRequestConfig): AxiosPromise<ClientStateResponse> {
            return localVarFp.getClientStateById(clientStateId, options).then((request) => request(axios, basePath));
        },
        /**
         * Returns all client states for the authenticated user\'s organization
         * @summary List client states
         * @param {boolean} [activeOnly] If true, only return active client states
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of items per page
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listClientStates(activeOnly?: boolean, page?: number, size?: number, options?: RawAxiosRequestConfig): AxiosPromise<ClientStateListResponse> {
            return localVarFp.listClientStates(activeOnly, page, size, options).then((request) => request(axios, basePath));
        },
        /**
         * Update a client state (requires client_states.update permission)
         * @summary Update client state
         * @param {string} clientStateId Client state UUID
         * @param {UpdateClientStateRequest} updateClientStateRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateClientState(clientStateId: string, updateClientStateRequest: UpdateClientStateRequest, options?: RawAxiosRequestConfig): AxiosPromise<ClientStateResponse> {
            return localVarFp.updateClientState(clientStateId, updateClientStateRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * ClientStatesApi - object-oriented interface
 */
export class ClientStatesApi extends BaseAPI {
    /**
     * Create a new client state (requires client_states.create permission)
     * @summary Create client state
     * @param {CreateClientStateRequest} createClientStateRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createClientState(createClientStateRequest: CreateClientStateRequest, options?: RawAxiosRequestConfig) {
        return ClientStatesApiFp(this.configuration).createClientState(createClientStateRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Delete/deactivate a client state (requires client_states.delete permission). If the state is in use by clients, it will be deactivated instead of deleted. 
     * @summary Delete client state
     * @param {string} clientStateId Client state UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteClientState(clientStateId: string, options?: RawAxiosRequestConfig) {
        return ClientStatesApiFp(this.configuration).deleteClientState(clientStateId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns a client state by ID
     * @summary Get client state by ID
     * @param {string} clientStateId Client state UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getClientStateById(clientStateId: string, options?: RawAxiosRequestConfig) {
        return ClientStatesApiFp(this.configuration).getClientStateById(clientStateId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns all client states for the authenticated user\'s organization
     * @summary List client states
     * @param {boolean} [activeOnly] If true, only return active client states
     * @param {number} [page] Page number (0-indexed)
     * @param {number} [size] Number of items per page
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listClientStates(activeOnly?: boolean, page?: number, size?: number, options?: RawAxiosRequestConfig) {
        return ClientStatesApiFp(this.configuration).listClientStates(activeOnly, page, size, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Update a client state (requires client_states.update permission)
     * @summary Update client state
     * @param {string} clientStateId Client state UUID
     * @param {UpdateClientStateRequest} updateClientStateRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateClientState(clientStateId: string, updateClientStateRequest: UpdateClientStateRequest, options?: RawAxiosRequestConfig) {
        return ClientStatesApiFp(this.configuration).updateClientState(clientStateId, updateClientStateRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



